import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'wouter';

interface FirstStoreContextType {
  firstStoreSlug: string | null;
  setFirstStoreSlug: (slug: string) => void;
  getHomeUrl: () => string;
}

const FirstStoreContext = createContext<FirstStoreContextType | undefined>(undefined);

const FIRST_STORE_KEY = 'pratigo_first_store_data';
const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

interface FirstStoreData {
  slug: string;
  timestamp: number;
}

interface FirstStoreProviderProps {
  children: React.ReactNode;
}

export function FirstStoreProvider({ children }: FirstStoreProviderProps) {
  const [location] = useLocation();
  const [firstStoreSlug, setFirstStoreSlugState] = useState<string | null>(() => {
    // Recupera e verifica se os dados da primeira loja ainda são válidos
    const storedData = localStorage.getItem(FIRST_STORE_KEY);
    if (!storedData) return null;

    try {
      const data: FirstStoreData = JSON.parse(storedData);
      const now = Date.now();
      const isExpired = now - data.timestamp > EXPIRATION_TIME;

      if (isExpired) {
        // Remove dados expirados
        localStorage.removeItem(FIRST_STORE_KEY);
        return null;
      }

      return data.slug;
    } catch {
      // Remove dados corrompidos
      localStorage.removeItem(FIRST_STORE_KEY);
      return null;
    }
  });

  const setFirstStoreSlug = (slug: string) => {
    setFirstStoreSlugState(slug);
    const data: FirstStoreData = {
      slug,
      timestamp: Date.now()
    };
    localStorage.setItem(FIRST_STORE_KEY, JSON.stringify(data));
  };

  const getHomeUrl = () => {
    return firstStoreSlug ? `/${firstStoreSlug}` : '/';
  };

  // Efeito para capturar o slug da primeira loja quando o usuário acessa a aplicação
  useEffect(() => {
    // Se já temos um slug salvo, não fazemos nada
    if (firstStoreSlug) {
      return;
    }

    // Verifica se a URL atual contém um slug de loja
    // Padrões de rota que indicam acesso a uma loja:
    // /:slug
    // /:slug/products/:productId
    // /:slug/order/
    const storeRoutePatterns = [
      /^\/([^/]+)$/,                    // /:slug
      /^\/([^/]+)\/products\/[^/]+$/,  // /:slug/products/:productId
      /^\/([^/]+)\/order\/?$/,          // /:slug/order/
    ];

    // Verifica se a URL atual não é uma das rotas especiais (auth, search, orders, profile)
    const specialRoutes = ['/auth', '/search', '/orders', '/profile', '/cart', '/login-by-link', '/entrar'];
    const isSpecialRoute = specialRoutes.some(route => location.startsWith(route));

    if (isSpecialRoute || location === '/') {
      return;
    }

    // Tenta extrair o slug da URL atual
    for (const pattern of storeRoutePatterns) {
      const match = location.match(pattern);
      if (match && match[1]) {
        const extractedSlug = match[1];
        console.log('🏪 Primeira loja detectada:', extractedSlug);
        setFirstStoreSlug(extractedSlug);
        break;
      }
    }
  }, [location, firstStoreSlug]);

  const contextValue: FirstStoreContextType = {
    firstStoreSlug,
    setFirstStoreSlug,
    getHomeUrl,
  };

  return (
    <FirstStoreContext.Provider value={contextValue}>
      {children}
    </FirstStoreContext.Provider>
  );
}

export function useFirstStore() {
  const context = useContext(FirstStoreContext);
  if (context === undefined) {
    throw new Error('useFirstStore must be used within a FirstStoreProvider');
  }
  return context;
}
